import { watch, ref } from 'vue'
import { AI_APP_CENTER_MODULES } from '@/constants/aiAppCenter/index'
import { AI_BUSINESS_CENTER_MODULES } from '@/constants/aiBusinessCenter/index'
import { useI18n } from '@/hooks/web/useI18n'
import { isString } from '@/utils/is'
import { captureStandaloneAppKey, findStandaloneAppConfig } from '@/utils/standaloneApp'
import { useAppStoreWithOut } from '@/store/modules/app'

const appStore = useAppStoreWithOut()

/** 已知路由的中文标题兜底（避免 meta.title 缺失/乱码/?? 时标签页异常） */
const PATH_TITLE_FALLBACKS: Array<[RegExp, string]> = [
  [/\/(?:ai-Application-center\/)?ai\/chat(?:\/|$)/, 'AI 对话'],
  [/\/(?:ai-Application-center\/)?ai\/image(?:\/|$)/, 'AI 绘画'],
  [/\/(?:ai-Application-center\/)?ai\/overview(?:\/|$)/, '概况'],
  [/\/(?:ai-Application-center\/)?ai\/write(?:\/|$)/, 'AI 写作'],
  [/\/(?:ai-Application-center\/)?ai\/music(?:\/|$)/, 'AI 音乐'],
  [/\/(?:ai-Application-center\/)?ai\/mind(?:\/|$)/, 'AI 思维导图'],
  [/\/(?:ai-Application-center\/)?live(?:\/|$)/, '直播管理']
]

const isGarbageTitle = (value: string) => {
  const text = value.trim()
  if (!text) return true
  // 仅问号 / 替换字符 / 空白控制符
  if (/^[?\uFFFD\s]+$/.test(text)) return true
  return false
}

/** 将路由 meta.title 解析为可读文案；i18n key 缺失时不回落到 ??? */
export const resolveRouteTitle = (raw?: unknown, pathname = window.location.pathname): string => {
  const i18nApi = useI18n() as { t: (key: string) => string; te?: (key: string) => boolean }
  const { t, te } = i18nApi
  const source = typeof raw === 'string' ? raw.trim() : ''

  if (source && !isGarbageTitle(source)) {
    // 普通中文/英文标题（非 a.b.c i18n key）直接使用
    if (!source.includes('.')) {
      return source
    }
    // i18n key：有翻译用翻译，否则用 key 最后一段，避免异常占位符
    if (typeof te === 'function' && te(source)) {
      const translated = t(source)
      if (typeof translated === 'string' && !isGarbageTitle(translated)) {
        return translated
      }
    }
    const last = source.split('.').filter(Boolean).pop()
    if (last && !isGarbageTitle(last) && !last.includes('.')) {
      return last
    }
  }

  const hit = PATH_TITLE_FALLBACKS.find(([pattern]) => pattern.test(pathname))
  return hit?.[1] || ''
}

export const useTitle = (newTitle?: string) => {
  const standaloneApp = findStandaloneAppConfig(
    [...AI_APP_CENTER_MODULES, ...AI_BUSINESS_CENTER_MODULES],
    captureStandaloneAppKey(window.location.search),
    window.location.pathname
  )
  const isAiModelPage =
    window.location.pathname.startsWith('/ai/') ||
    window.location.pathname.startsWith('/ai-Application-center/ai/')
  // 本地/独立窗优先用模块中文名，避免落到系统默认英文/项目名
  const appTitle =
    standaloneApp?.label ||
    (isAiModelPage ? 'AI大模型' : '') ||
    appStore.getTitle ||
    '紫金山AI'
  const pageTitle = resolveRouteTitle(newTitle, window.location.pathname)
  const title = ref(pageTitle ? `${appTitle} - ${pageTitle}` : appTitle)

  watch(
    title,
    (n, o) => {
      if (isString(n) && n !== o && document) {
        document.title = n
      }
    },
    { immediate: true }
  )

  return title
}
